/**
 * ClosingCtaSection — section 8 of the homepage (Konzept § 5 Z. 133, § 7).
 *
 * Job: convert. This is the *second and last* appearance of the primary CTA
 * (KI-Potenzialanalyse); the header carries the first. Everything between them
 * exits through its own section link instead.
 *
 * The secondary action is the personal route ("Rede mit Michael", video call) —
 * a KMU advantage worth keeping (Konzept § 7 Z. 204), but never the primary one.
 * Button localises hrefs itself and detects external links, so the German
 * canonical paths and the booking URL both pass through unchanged.
 */

import React from "react";
import { type ClosingCtaActionProps, type SectionClosingCtaProps } from "../../content/section-closing-cta";
import { Button, RichText, SectionContainer } from "../ui";
import { ContactRequestModal } from "../contactRequest/ContactRequestModal";
import { ChallengeRequestModal } from "../challengeRequest/ChallengeRequestModal";

/**
 * Wie in `ContactSection`: ein Link auf den geteilten Google-Kalender heisst
 * «Termin vereinbaren», und genau das leistet das Modal jetzt — ohne dass der
 * Besuch die Seite verlaesst. Echte Navigation bleibt Navigation.
 */
const isCalendarBooking = (href?: string) => Boolean(href?.includes("calendar.app.google"));

/**
 * Oeffnet dieser Knopf den Dialog?
 *
 * Zwei Wege zur selben Antwort, und das ausdrueckliche Flag gewinnt: Der
 * Content sagt es direkt, die Kalender-Erkennung erraet es aus der `href`.
 * Raten bleibt zulaessig, weil die bestehenden Seiten darauf stehen — aber wo
 * beides gilt, zaehlt die Aussage vor dem Indiz.
 */
const opensDialog = (action?: ClosingCtaActionProps) => {
    if (!action) {
        return false;
    }
    // Gesetzt heisst gesetzt — auch `false`. Sonst koennte eine Seite, die
    // einen Kalenderlink ausdruecklich NICHT als Dialog will, das nicht sagen.
    if (action.opensDialog !== undefined) {
        return action.opensDialog;
    }
    return isCalendarBooking(action.href);
};

export type ClosingCtaSectionProps = SectionClosingCtaProps & {
    /**
     * Ob diese Sektion den «#kontakt»-Anker traegt. Standard `true` — auf den
     * rund 17 Seiten, die den Abschluss-Block ohne Kontakt-Sektion fuehren,
     * ist er der einzige Kandidat.
     *
     * Die Startseite setzt `false` (v0.3 F5): sie hat neu eine echte
     * Kontakt-Sektion mit Ansprechpartner, und «#kontakt» soll bei einem
     * Menschen landen statt bei zwei bezahlten Angeboten. Zwei Elemente mit
     * derselben id waeren ausserdem ungueltiges HTML.
     */
    anchor?: boolean;
    /**
     * Teil eines Kapitels statt eigenstaendige Sektion? (12.8.2026)
     *
     * Im Kapitel «Fangen wir an» der Startseite sind Ueberschrift und Absatz
     * DIESER Sektion die Kapitelueberschrift — sie stehen eine Ebene hoeher in
     * `ChapterSection`, gefolgt von der Kontaktperson als `h3`. Uebrig bleiben
     * hier die zwei Knoepfe.
     *
     * Der Grund fuer die Fusion: Auf `/referenzen` war am 11.8. genau das der
     * Befund — ein Handlungsaufruf ohne Namen, ohne Telefonnummer und ohne
     * Terminlink. Knopf und Mensch gehoeren zusammen.
     */
    nested?: boolean;
    /**
     * Steht die Sektion auf dunklem Grund? Wie `onDark` an `Button` und wie
     * `variant="dark"` an `ContactSection`. Das Abschluss-Kapitel traegt den
     * Farbverlauf, damit die Seite auf einem Block endet statt auf zweien.
     */
    onDark?: boolean;
    /**
     * Welcher Dialog aufgeht, wenn ein Knopf einen oeffnet (21.9.2026).
     *
     * Standard `contact` — der Dialog, den die Website ueberall fuehrt. Die
     * RFP-Kampagnenseite setzt `challenge` und bekommt damit ihren eigenen
     * Wortlaut, ohne dass der gemeinsame Dialog davon eine Zeile sieht.
     *
     * Eine Prop und kein Content-Feld: Die `closingCta`-Sektion steht auf 27
     * Seiten, und 26 davon haben zu dieser Frage nichts zu sagen. Ein Feld im
     * geteilten Schema haette sie alle gefragt.
     */
    dialog?: "contact" | "challenge";
    /**
     * Thema der Anfrage im Dialog. Ohne Angabe gilt der Sektionstitel.
     *
     * Der Titel der Ausschreibungen-Seite heisst «Challengen Sie uns.», und mit
     * diesem Thema kam jede Challenge in Slack, Mail und Kalender an, ohne
     * Hinweis auf die Seite (QUAWEB-42).
     */
    topic?: string;
    /**
     * Kennung der Kampagnenseite, z.B. `ausschreibungen`. Das Formular meldet
     * sie als `campaign_page` an der Conversion `contact_request` (QUAWEB-42).
     */
    campaignPage?: string;
    /**
     * Einleitung des Challenge-Dialogs. Ohne Angabe gilt der globale Text aus
     * `components/challenge-request.mdx`. Nur der Challenge-Dialog liest sie
     * (QUAWEB-40).
     */
    dialogIntro?: string;
};

export const ClosingCtaSection: React.FC<ClosingCtaSectionProps> = ({
    title,
    description,
    primary,
    secondary,
    anchor = true,
    nested = false,
    onDark = false,
    dialog = "contact",
    topic,
    campaignPage,
    dialogIntro,
}) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    // Diese Sektion fuehrt keine `person` — anders als die ContactSection kann
    // sie den Empfaenger also nicht aus dem Content ableiten. Ohne Angabe faellt
    // die Service-Funktion auf INTERNAL_MAIL_RECIPIENT und dann auf
    // info@quatico.com zurueck; das Thema traegt `topic` oder den Sektionstitel.
    const primaryOpensModal = opensDialog(primary);
    const secondaryOpensModal = opensDialog(secondary);

    return (
        <section
            data-testid="closing-cta-section"
            id={anchor ? "kontakt" : undefined}
            className={nested ? "" : "bg-gray-50 py-24"}
        >
            {/* Eine Ebene statt zwei: der innere `max-w-3xl` war identisch mit
                der Textspur des SectionContainer. */}
            <SectionContainer width="text" className="text-center">
                {!nested && (
                    <>
                        <h2 className="mb-6 text-3xl font-black text-gray-900 md:text-4xl">{title}</h2>
                        {/* `RichText` statt `{description}` (10.9.2026): Der Abschluss-Block
                            ist der Ort, an dem ein weiterfuehrender Verweis hingehoert — der
                            Leser hat die Seite gelesen und waegt ab. Als reiner Text erschien
                            ein `<a href>` sichtbar als Markup.

                            Rueckwaertskompatibel: Keine der 29 `closingCta`-Beschreibungen im
                            Content fuehrt Markup (nachgezaehlt), reiner Text rendert
                            unveraendert. */}
                        <RichText content={description} className="mb-10 block text-lg leading-relaxed text-gray-600" />
                    </>
                )}
                <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                    {primaryOpensModal ? (
                        <Button onClick={() => setIsModalOpen(true)} variant="primary" size="lg" onDark={onDark}>
                            {primary.text}
                        </Button>
                    ) : (
                        <Button href={primary.href} variant="primary" size="lg" onDark={onDark}>
                            {primary.text}
                        </Button>
                    )}
                    {secondary &&
                        (secondaryOpensModal ? (
                            <Button onClick={() => setIsModalOpen(true)} variant="secondary" size="lg" onDark={onDark}>
                                {secondary.text}
                            </Button>
                        ) : (
                            <Button href={secondary.href} variant="secondary" size="lg" onDark={onDark}>
                                {secondary.text}
                            </Button>
                        ))}
                </div>
            </SectionContainer>

            {isModalOpen &&
                (dialog === "challenge" ? (
                    <ChallengeRequestModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        topic={topic ?? title}
                        campaignPage={campaignPage}
                        dialogIntro={dialogIntro}
                    />
                ) : (
                    <ContactRequestModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        topic={topic ?? title}
                        campaignPage={campaignPage}
                    />
                ))}
        </section>
    );
};

export default ClosingCtaSection;
